import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRotate } from '@fortawesome/free-solid-svg-icons';
import { getClientQueries, raiseClientQuery } from '../../service/apiService';
import './ClientSupport.css';

const emptyForm = {
  project_id: '',
  subject: '',
  priority: 'normal',
  message: '',
};

function statusVariant(status) {
  return {
    open: 'primary',
    in_progress: 'warning',
    resolved: 'success',
    closed: 'secondary',
  }[status] || 'secondary';
}

function ClientSupport({ user }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [queries, setQueries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [client, setClient] = useState(null);

  const loadQueries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientQueries();
      if (res.status === 'success') {
        setQueries(res.data?.queries || []);
        setProjects(res.data?.projects || []);
        setClient(res.data?.client || null);
      } else {
        setMessage(res.message || 'Unable to load queries');
      }
    } catch (error) {
      console.error('Client query load error:', error);
      setMessage('Unable to load queries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const res = await raiseClientQuery(form);
      if (res.status === 'success') {
        setForm(emptyForm);
        setMessage('Your query has been raised successfully');
        await loadQueries();
      } else {
        setMessage(res.message || 'Unable to raise query');
      }
    } catch (error) {
      console.error('Client query save error:', error);
      setMessage('Unable to raise query');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="client-support-page">
      <div className="page-title-row">
        <div>
          <span>Client Portal</span>
          <h3>Project Queries</h3>
          <p className="text-muted mb-0">Raise questions or support tickets about your active project work.</p>
        </div>
        <Button variant="outline-secondary" onClick={loadQueries}>
          <FontAwesomeIcon icon={faRotate} />
        </Button>
      </div>

      {message && <Alert variant="info" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Row className="g-3">
        <Col lg={4}>
          <Card className="erp-panel-card">
            <Card.Body>
              <div className="text-muted small mb-1">Logged in client</div>
              <h5 className="mb-1">{client?.client_name || user?.name}</h5>
              <div className="text-muted small">{client?.phone || user?.username}</div>
            </Card.Body>
          </Card>

          <Card className="erp-panel-card mt-3">
            <Card.Body>
              <h5 className="mb-3">Raise Query</h5>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Project</Form.Label>
                  <Form.Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                    <option value="">General query</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.project_name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Query Details</Form.Label>
                  <Form.Control as="textarea" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
                </Form.Group>
                <Button type="submit" disabled={saving} className="w-100">
                  <FontAwesomeIcon icon={faPaperPlane} /> {saving ? 'Submitting...' : 'Submit Query'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="erp-panel-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">My Queries</h5>
                <Badge bg="primary">{queries.length}</Badge>
              </div>
              {loading ? <div className="table-loader"><Spinner animation="border" /></div> : (
                <Table responsive hover className="erp-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Project</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Response</th>
                      <th>Raised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queries.map((query) => (
                      <tr key={query.id}>
                        <td>
                          <strong>{query.subject}</strong>
                          <div className="text-muted small">{query.message}</div>
                        </td>
                        <td>{query.project_name || 'General'}</td>
                        <td><Badge bg={query.priority === 'urgent' ? 'danger' : query.priority === 'high' ? 'warning' : 'secondary'}>{query.priority}</Badge></td>
                        <td><Badge bg={statusVariant(query.status)}>{String(query.status || '').replace('_', ' ')}</Badge></td>
                        <td>{query.admin_response || '-'}</td>
                        <td>{query.created_at ? new Date(query.created_at).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                    {!queries.length && <tr><td colSpan="6" className="text-center text-muted">No queries raised yet</td></tr>}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ClientSupport;
